/**
 * Onboarding API Client
 * Handles all onboarding-related API calls
 *
 * ✅ Auth hybride:
 *   - accessToken → header Authorization: Bearer
 *   - refreshToken → cookie HTTP-only (credentials: include)
 */

import { getAccessToken } from "../services/tokenManager";
import { safeFetch, safeJsonParse } from './api';
import { logger } from './logger';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

// Helper pour les requêtes authentifiées
const getAuthHeaders = () => {
  const token = getAccessToken();
  logger.log('🔑 onboardingAPI: getAuthHeaders called', {
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 20)}...` : null,
  });
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Options de fetch avec credentials pour cookies HTTP-only + Authorization header
const fetchOptions = (): RequestInit => ({
  credentials: 'include' as RequestCredentials,
  headers: getAuthHeaders(),
});

// Types
export interface OnboardingProgress {
  id: string;
  userId: string;
  tenantId: string;
  companyCreated: boolean;
  logoUploaded: boolean;
  legalMentionsAdded: boolean;
  paymentTermsConfigured: boolean;
  firstClientAdded: boolean;
  firstProductAdded: boolean;
  firstQuoteCreated: boolean;
  isCompleted: boolean;
  completedAt: Date | null;
  skippedAt: Date | null;
  currentStep: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface OnboardingStatus {
  progress: OnboardingProgress | null;
  stepsCompleted: number;
  totalSteps: number;
  completionPercentage: number;
  nextStep: string | null;
}

export interface OnboardingStats {
  total: number;
  completed: number;
  rate: number;
}

/**
 * Onboarding API
 * ✅ Toutes les requêtes utilisent credentials: 'include' pour les cookies HTTP-only
 */
export const onboardingAPI = {
  /**
   * Récupérer la progression de l'utilisateur courant
   */
  getProgress: async (): Promise<OnboardingStatus> => {
    logger.log('🔄 onboardingAPI.getProgress: Making request to', `${API_URL}/onboarding/progress`);

    const options = fetchOptions();
    logger.log('🔄 onboardingAPI.getProgress: Request options', {
      credentials: options.credentials,
      hasAuthHeader: !!(options.headers as Record<string, string>)?.['Authorization'],
    });

    const response = await safeFetch(`${API_URL}/onboarding/progress`, options);

    logger.log('🔄 onboardingAPI.getProgress: Response status', response.status);

    if (!response.ok) {
      const error = await safeJsonParse(response);
      logger.error('❌ onboardingAPI.getProgress: Error response', {
        status: response.status,
        error: error,
      });
      throw new Error(error.error || "Erreur lors de la récupération de la progression");
    }

    const json = await safeJsonParse(response);
    return json.data;
  },

  /**
   * Mettre à jour une étape spécifique
   */
  updateStep: async (step: string, completed: boolean): Promise<OnboardingProgress> => {
    const response = await safeFetch(`${API_URL}/onboarding/step`, {
      ...fetchOptions(),
      method: "POST",
      body: JSON.stringify({ step, completed }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || "Erreur lors de la mise à jour de l'étape");
    }

    const json = await safeJsonParse(response);
    return json.data;
  },

  /**
   * Passer à l'étape suivante
   */
  nextStep: async (): Promise<OnboardingStatus> => {
    const response = await safeFetch(`${API_URL}/onboarding/next`, {
      ...fetchOptions(),
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || "Erreur lors du passage à l'étape suivante");
    }

    const json = await safeJsonParse(response);
    return json.data;
  },

  /**
   * Marquer l'onboarding comme terminé
   */
  complete: async (): Promise<OnboardingProgress> => {
    const response = await safeFetch(`${API_URL}/onboarding/complete`, {
      ...fetchOptions(),
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || "Erreur lors de la finalisation de l'onboarding");
    }

    const json = await safeJsonParse(response);
    return json.data;
  },

  /**
   * Passer l'onboarding
   */
  skip: async (): Promise<OnboardingProgress> => {
    const response = await safeFetch(`${API_URL}/onboarding/skip`, {
      ...fetchOptions(),
      method: "POST",
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || "Erreur lors du skip de l'onboarding");
    }

    const json = await safeJsonParse(response);
    return json.data;
  },

  /**
   * Récupérer les statistiques du tenant (admin only)
   */
  getStats: async (): Promise<OnboardingStats> => {
    const response = await safeFetch(`${API_URL}/onboarding/stats`, {
      ...fetchOptions(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || "Erreur lors de la récupération des statistiques");
    }

    const json = await safeJsonParse(response);
    return json.data;
  },
};
